import {
  FilaPrestacion,
  FilaCalculada,
  PlanConfig,
  ResultadoPlan,
  ResultadoConsolidado,
  EscenarioData,
} from '../../types/Calculadora';
import { PurchaserPlanRaw } from '../../types/Dashboard';
import { activesAtDate, exclusive8080AutoRevenue, today } from '../dashboard/dashboardHelpers';
import { PLAN_POR_EDAD, tramosEdadVacios } from '../../data/calculadoraDefaults';
import { Tone } from '../ui';

// ── Formato ────────────────────────────────────────────────────────────────

const currencyFormatter = new Intl.NumberFormat('es-AR', {
  style: 'currency',
  currency: 'ARS',
  maximumFractionDigits: 0,
});

const currencyFormatter2 = new Intl.NumberFormat('es-AR', {
  style: 'currency',
  currency: 'ARS',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** Moneda sin decimales. Para agregados grandes: KPIs, consolidado, gráficos. */
export function formatCurrency(value: number): string {
  if (!Number.isFinite(value)) return '—';
  return currencyFormatter.format(value);
}

/**
 * Moneda con dos decimales, formato argentino ("$ 22.244,80"). Para valores
 * unitarios por prestación, donde los centavos importan y tiene que matchear
 * a los inputs de la tabla.
 */
export function formatCurrency2(value: number): string {
  if (!Number.isFinite(value)) return '—';
  return currencyFormatter2.format(value);
}

/** Montos grandes en los ejes de los gráficos: $12,4M / $980k. */
export function formatCompact(value: number): string {
  if (!Number.isFinite(value)) return '—';
  const abs = Math.abs(value);
  const signo = value < 0 ? '-' : '';
  if (abs >= 1_000_000) return `${signo}$${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${signo}$${Math.round(abs / 1_000)}k`;
  return `${signo}$${Math.round(abs)}`;
}

export function formatNumber(value: number): string {
  if (!Number.isFinite(value)) return '—';
  return value.toLocaleString('es-AR', { maximumFractionDigits: 0 });
}

export function formatPct(value: number, decimals = 1): string {
  if (!Number.isFinite(value)) return '—';
  return `${value.toFixed(decimals)}%`;
}

/** Number() tolerante: descarta NaN y opcionalmente recorta a un rango. */
export function toNumber(raw: string | number, min = -Infinity, max = Infinity): number {
  const parsed = typeof raw === 'number' ? raw : Number(String(raw).replace(',', '.'));
  if (!Number.isFinite(parsed)) return 0;
  return Math.min(Math.max(parsed, min), max);
}

// ── El cálculo de una fila ─────────────────────────────────────────────────

/**
 * Resuelve la fila según su modo.
 *
 *   modo 'gappex'     → se fija el Precio Gappex, sale el Digital
 *   modo 'prestador'  → se fija el Digital, sale el Precio Gappex
 *
 * El pago del paciente NO se topea contra el Precio Gappex: si el particular
 * con descuento queda por encima de la tarifa, el prestador cobra de más y
 * Digital no pone nada. Eso es lo que refleja el max(0, …).
 */
export function calcularFila(fila: FilaPrestacion): FilaCalculada {
  const descuento = Math.min(Math.max(fila.descuentoPct, 0), 100);
  const pagoPaciente = fila.precioParticular * (1 - descuento / 100);

  let precioGappexEfectivo: number;
  let digitalEfectivo: number;

  if (fila.modo === 'gappex') {
    precioGappexEfectivo = fila.precioGappex;
    digitalEfectivo = Math.max(0, fila.precioGappex - pagoPaciente);
  } else {
    digitalEfectivo = Math.max(0, fila.digitalPrestador);
    precioGappexEfectivo = pagoPaciente + digitalEfectivo;
  }

  const cuenta = fila.activa;
  const descuentoEfectivoPct = fila.precioParticular > 0
    ? ((fila.precioParticular - pagoPaciente) / fila.precioParticular) * 100
    : 0;

  return {
    fila,
    pagoPaciente,
    precioGappexEfectivo,
    digitalEfectivo,
    costoAnualMin: cuenta ? digitalEfectivo * fila.cantMin : 0,
    costoAnualMax: cuenta ? digitalEfectivo * fila.cantMax : 0,
    // Solo tiene sentido marcarlo cuando el plan da descuento Y la fila tiene
    // precios cargados: una fila en cero no es "beneficio gratis", está vacía.
    sinCostoParaGappex:
      cuenta && digitalEfectivo === 0 && descuento > 0 && fila.precioParticular > 0,
    descuentoEfectivoPct,
  };
}

// ── El cálculo de un plan ──────────────────────────────────────────────────

/** Cuota mensual promedio: la plana, o la ponderada por el mix etario. */
export function cuotaPromedioDePlan(config: PlanConfig): number {
  if (!config.usaDistribucionEdad) return config.cuotaMensual;

  const pctTotal = config.distribucionEdad.reduce((acc, t) => acc + t.pct, 0);
  if (pctTotal <= 0) return 0;

  // Se normaliza por el total cargado, así un mix que suma 98% o 103% por
  // redondeo sigue dando una cuota promedio sensata.
  const ponderado = config.distribucionEdad.reduce((acc, t) => acc + t.pct * t.valor, 0);
  return ponderado / pctTotal;
}

export function calcularPlan(config: PlanConfig): ResultadoPlan {
  const filas = config.filas.map(calcularFila);

  const costoPerCapitaMin = filas.reduce((acc, f) => acc + f.costoAnualMin, 0);
  const costoPerCapitaMax = filas.reduce((acc, f) => acc + f.costoAnualMax, 0);
  const costoPerCapitaProm = (costoPerCapitaMin + costoPerCapitaMax) / 2;

  const cuotaPromedio = cuotaPromedioDePlan(config);
  const ingresoMensual = config.suscriptores * cuotaPromedio;
  const ingresoAnual = ingresoMensual * 12;

  const expuestos = config.suscriptores * config.tasaUso;
  const gastoAnualMin = expuestos * costoPerCapitaMin;
  const gastoAnualProm = expuestos * costoPerCapitaProm;
  const gastoAnualMax = expuestos * costoPerCapitaMax;

  const resultadoAnualProm = ingresoAnual - gastoAnualProm;

  return {
    planId: config.planId,
    nombre: config.nombre,
    ficticio: config.ficticio,
    incluido: config.incluido,
    suscriptores: config.suscriptores,
    cuotaPromedio,
    ingresoMensual,
    ingresoAnual,
    costoPerCapitaMin,
    costoPerCapitaProm,
    costoPerCapitaMax,
    gastoAnualMin,
    gastoAnualProm,
    gastoAnualMax,
    // El escenario "mínimo" es el de menor gasto, o sea el mejor resultado.
    resultadoAnualMin: ingresoAnual - gastoAnualMin,
    resultadoAnualProm,
    resultadoAnualMax: ingresoAnual - gastoAnualMax,
    margenPct: ingresoAnual > 0 ? (resultadoAnualProm / ingresoAnual) * 100 : 0,
    filas,
  };
}

// ── El consolidado del mix ─────────────────────────────────────────────────

export function calcularConsolidado(data: EscenarioData): ResultadoConsolidado {
  const porPlan = data.planes.map(calcularPlan);
  const incluidos = porPlan.filter((p) => p.incluido);

  const sum = (pick: (p: ResultadoPlan) => number) =>
    incluidos.reduce((acc, p) => acc + pick(p), 0);

  const ingresoAnual = sum((p) => p.ingresoAnual);
  const gastoAnualProm = sum((p) => p.gastoAnualProm);
  const resultadoAnualProm = ingresoAnual - gastoAnualProm;

  return {
    suscriptores: sum((p) => p.suscriptores),
    ingresoMensual: sum((p) => p.ingresoMensual),
    ingresoAnual,
    gastoAnualMin: sum((p) => p.gastoAnualMin),
    gastoAnualProm,
    gastoAnualMax: sum((p) => p.gastoAnualMax),
    resultadoAnualMin: ingresoAnual - sum((p) => p.gastoAnualMin),
    resultadoAnualProm,
    resultadoAnualMax: ingresoAnual - sum((p) => p.gastoAnualMax),
    margenPct: ingresoAnual > 0 ? (resultadoAnualProm / ingresoAnual) * 100 : 0,
    porPlan,
  };
}

// ── Prellenado con los datos reales de Salesforce ──────────────────────────

export interface DatosRealesResumen {
  /** Planes a los que se les cargó población. */
  planesActualizados: number;
  suscriptoresTotales: number;
  /** Activos del 8080 sin fecha de nacimiento: no entran en la distribución. */
  sinFechaNacimiento: number;
}

export interface PrellenadoResultado {
  data: EscenarioData;
  resumen: DatosRealesResumen;
}

/**
 * Pisa suscriptores y cuota de cada plan con lo que hay hoy en Salesforce.
 * Los precios de las prestaciones NO se tocan: eso lo define el negocio.
 *
 * Para el Exclusive 8080 además arma la distribución etaria real en % sobre
 * los activos que tienen fecha de nacimiento cargada.
 */
export function prellenarConDatosReales(
  data: EscenarioData,
  premium: PurchaserPlanRaw[],
  freeActivos: { plan: string; cantidad: number }[],
  precios: Record<string, number>,
): PrellenadoResultado {
  const fecha = today();
  const activosPremium = activesAtDate(premium, fecha);

  const activosPorPlan: Record<string, number> = { ...activosPremium };
  for (const row of freeActivos) {
    activosPorPlan[row.plan] = (activosPorPlan[row.plan] ?? 0) + row.cantidad;
  }

  const auto = exclusive8080AutoRevenue(premium, fecha, PLAN_POR_EDAD);

  // Distribución etaria real: cuántos activos caen en cada tramo, en %.
  const conteoPorTramo = new Map<string, number>();
  for (const detalle of auto.details) {
    if (detalle.bracket === 'missing') continue;
    conteoPorTramo.set(detalle.bracket, (conteoPorTramo.get(detalle.bracket) ?? 0) + 1);
  }

  let planesActualizados = 0;
  let suscriptoresTotales = 0;

  const planes = data.planes.map((config) => {
    const suscriptores = activosPorPlan[config.nombre];
    if (suscriptores === undefined) return config;

    planesActualizados += 1;
    suscriptoresTotales += suscriptores;

    const precio = precios[config.nombre];
    const siguiente: PlanConfig = {
      ...config,
      suscriptores,
      cuotaMensual: typeof precio === 'number' ? precio : config.cuotaMensual,
    };

    if (config.usaDistribucionEdad && auto.withBirthdate > 0) {
      const base = config.distribucionEdad.length > 0 ? config.distribucionEdad : tramosEdadVacios();
      siguiente.distribucionEdad = base.map((tramo) => ({
        ...tramo,
        pct: Math.round(((conteoPorTramo.get(tramo.label) ?? 0) / auto.withBirthdate) * 1000) / 10,
      }));
    }

    return siguiente;
  });

  return {
    data: { ...data, planes, fechaPrecios: fecha },
    resumen: {
      planesActualizados,
      suscriptoresTotales,
      sinFechaNacimiento: auto.missingBirthdate,
    },
  };
}

// ── Utilidades de edición ──────────────────────────────────────────────────

/**
 * Copia la tabla de prestaciones de un plan al resto, respetando el % de
 * descuento que le corresponde a cada plan en cada categoría. Se copian los
 * precios y las cantidades, no la cobertura.
 */
export function aplicarFilasATodos(
  data: EscenarioData,
  planIdOrigen: string,
  descuentoPara: (planId: string, fila: FilaPrestacion) => number,
): EscenarioData {
  const origen = data.planes.find((p) => p.planId === planIdOrigen);
  if (!origen) return data;

  return {
    ...data,
    planes: data.planes.map((config) => {
      if (config.planId === planIdOrigen) return config;

      const previas = new Map(config.filas.map((f) => [f.id, f]));

      return {
        ...config,
        filas: origen.filas.map((fila) => {
          const previa = previas.get(fila.id);
          return {
            ...fila,
            // Cada plan conserva su cobertura: el % y el encendido no se pisan.
            descuentoPct: previa ? previa.descuentoPct : descuentoPara(config.planId, fila),
            activa: previa ? previa.activa : fila.activa,
          };
        }),
      };
    }),
  };
}

// ── Punto de equilibrio (break-even) ────────────────────────────────────────
//
// El ingreso no depende del uso; el gasto sí, de forma lineal. Entonces
//   resultado(x) = ingresoAnual − slope · x
// cruza cero en  x* = ingresoAnual / slope.
//
// Para un plan, x es la tasa de uso (0..1) y slope = suscriptores · costo per
// cápita. Para el consolidado, x es un multiplicador del uso de todo el mix
// (hoy = 1) y slope = gasto anual promedio actual.


export interface BreakEven {
  /** Modo del eje x: fracción de uso, o multiplicador del uso actual. */
  modo: 'tasa' | 'multiplicador';
  ingresoAnual: number;
  /** Pendiente del gasto en el mejor caso de consumo (costo mínimo). */
  slopeMin: number;
  /** Pendiente del gasto con consumo promedio. */
  slope: number;
  /** Pendiente del gasto en el peor caso de consumo (costo máximo). */
  slopeMax: number;
  /** x actual (tasa del plan, o 1 en el consolidado). */
  currentX: number;
  /** x de equilibrio con consumo promedio. null si no hay costo variable. */
  breakEvenX: number | null;
  /** x de equilibrio en el mejor caso (costo mín): aguanta MÁS uso. */
  breakEvenXMejor: number | null;
  /** x de equilibrio en el peor caso (costo máx): aguanta MENOS uso. */
  breakEvenXPeor: number | null;
}

export function breakEvenDePlan(
  ingresoAnual: number,
  suscriptores: number,
  costoPerCapitaMin: number,
  costoPerCapitaProm: number,
  costoPerCapitaMax: number,
  tasaUso: number,
): BreakEven {
  const slopeMin = suscriptores * costoPerCapitaMin;
  const slope = suscriptores * costoPerCapitaProm;
  const slopeMax = suscriptores * costoPerCapitaMax;
  return {
    modo: 'tasa',
    ingresoAnual,
    slopeMin,
    slope,
    slopeMax,
    currentX: tasaUso,
    breakEvenX: slope > 0 ? ingresoAnual / slope : null,
    breakEvenXMejor: slopeMin > 0 ? ingresoAnual / slopeMin : null,
    breakEvenXPeor: slopeMax > 0 ? ingresoAnual / slopeMax : null,
  };
}

export function breakEvenDeConsolidado(
  ingresoAnual: number,
  gastoAnualMin: number,
  gastoAnualProm: number,
  gastoAnualMax: number,
): BreakEven {
  return {
    modo: 'multiplicador',
    ingresoAnual,
    slopeMin: gastoAnualMin,
    slope: gastoAnualProm,
    slopeMax: gastoAnualMax,
    currentX: 1,
    breakEvenX: gastoAnualProm > 0 ? ingresoAnual / gastoAnualProm : null,
    breakEvenXMejor: gastoAnualMin > 0 ? ingresoAnual / gastoAnualMin : null,
    breakEvenXPeor: gastoAnualMax > 0 ? ingresoAnual / gastoAnualMax : null,
  };
}

export interface BreakEvenLectura {
  /** Valor formateado del equilibrio promedio, ej. "62% de uso" o "×1,8". */
  valor: string;
  /** Etiqueta de dónde estás hoy, ej. "hoy 40%" o "hoy ×1,0". */
  hoy: string;
  /** Veredicto corto: "Margen amplio", "Al límite", "En pérdida"… */
  etiqueta: string;
  tono: Tone;
  /** Rango peor→mejor caso de consumo, ej. "45% a 90%". null si no aplica. */
  rango: string | null;
}

/** Formatea un x según el modo (tasa como %, multiplicador como ×N). */
export function formatBreakEvenX(modo: 'tasa' | 'multiplicador', x: number): string {
  if (modo === 'tasa') return `${Math.round(x * 100)}%`;
  return `×${x.toLocaleString('es-AR', { minimumFractionDigits: 1, maximumFractionDigits: 2 })}`;
}

/** Igual que formatBreakEvenX pero en modo tasa capea arriba del 100%. */
function formatBreakEvenXCap(modo: 'tasa' | 'multiplicador', x: number): string {
  if (modo === 'tasa' && x > 1) return '+100%';
  return formatBreakEvenX(modo, x);
}

export function leerBreakEven(be: BreakEven): BreakEvenLectura {
  const hoy = be.modo === 'tasa' ? `hoy ${formatBreakEvenX('tasa', be.currentX)}` : `hoy ×1,0`;

  // Rango peor→mejor (peor = costo máx, aguanta menos; mejor = costo mín).
  let rango: string | null = null;
  if (be.breakEvenXPeor !== null && be.breakEvenXMejor !== null) {
    const peor = formatBreakEvenXCap(be.modo, be.breakEvenXPeor);
    const mejor = formatBreakEvenXCap(be.modo, be.breakEvenXMejor);
    rango = peor === mejor ? null : `${peor} a ${mejor}`;
  }

  if (be.breakEvenX === null) {
    return { valor: 'Sin costo variable', hoy, etiqueta: 'No da pérdida por uso', tono: 'positive', rango: null };
  }

  // En modo tasa el uso no puede pasar del 100%: si el equilibrio queda arriba,
  // el plan es rentable aunque lo use todo el mundo.
  if (be.modo === 'tasa' && be.breakEvenX > 1) {
    return { valor: 'Rentable al 100% de uso', hoy, etiqueta: 'Margen amplio', tono: 'positive', rango };
  }

  const valor =
    be.modo === 'tasa'
      ? `${formatBreakEvenX('tasa', be.breakEvenX)} de uso`
      : `${formatBreakEvenX('multiplicador', be.breakEvenX)} el uso`;

  if (be.currentX >= be.breakEvenX) {
    return { valor, hoy, etiqueta: 'En pérdida', tono: 'negative', rango };
  }

  const consumido = be.currentX / be.breakEvenX; // qué fracción del margen ya usás
  let etiqueta = 'Margen amplio';
  let tono: Tone = 'positive';
  if (consumido >= 0.9) {
    etiqueta = 'Al límite';
    tono = 'negative';
  } else if (consumido >= 0.75) {
    etiqueta = 'Margen ajustado';
    tono = 'warning';
  }
  return { valor, hoy, etiqueta, tono, rango };
}
